import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, AlertTriangle, TrendingUp, Search, X, Sparkles, RefreshCw } from "lucide-react";
import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import { BACKEND_URL } from "../lib/backendUrl";

const BACKEND = BACKEND_URL;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtINR(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Math.abs(Number(n));
  const sign = n < 0 ? "-" : "";
  if (v >= 1_00_00_000) return `${sign}₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000)    return `${sign}₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1000)        return `${sign}₹${(v / 1000).toFixed(1)}K`;
  return `${sign}₹${Math.round(v).toLocaleString("en-IN")}`;
}

function riskTone(level) {
  switch ((level || "").toLowerCase()) {
    case "low risk":    return { bg: "#DCFCE7", fg: "#166534", dot: "#10B981" };
    case "medium risk": return { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6" };
    case "caution":     return { bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" };
    case "high risk":   return { bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" };
    default:            return { bg: "#F3F4F6", fg: "#374151", dot: "#9CA3AF" };
  }
}

function scoreBarColor(score) {
  if (score >= 80) return "#10B981";
  if (score >= 60) return "#3B82F6";
  if (score >= 40) return "#F59E0B";
  return "#EF4444";
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({ icon: Icon, label, value, sub, tone = "blue" }) {
  const tones = {
    blue:  { bg: "#EFF6FF", fg: "#1D4ED8" },
    green: { bg: "#DCFCE7", fg: "#166534" },
    red:   { bg: "#FEE2E2", fg: "#B91C1C" },
    amber: { bg: "#FEF3C7", fg: "#92400E" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1 }}>
          {label}
        </div>
        <div style={{ background: t.bg, color: t.fg, width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={15} />
        </div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#0F1B2D", marginTop: 8 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verify Form (manual one-supplier verification)
// ---------------------------------------------------------------------------
function VerifyForm({ customerId, onDone }) {
  const [name, setName] = useState("");
  const [gstin, setGstin] = useState("");
  const [city, setCity] = useState("");
  const [outstanding, setOutstanding] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError("Supplier name is required"); return; }
    setError(""); setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/supplier/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          supplier_name: name.trim(),
          gstin: gstin.trim().toUpperCase(),
          city: city.trim(),
          outstanding_amount: parseInt(outstanding) || 0,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Verify failed");
      onDone?.(j);
      setName(""); setGstin(""); setCity(""); setOutstanding("");
    } catch (err) {
      setError(err.message || "Could not verify supplier");
    } finally {
      setBusy(false);
    }
  };

  const input = {
    flex: 1, minWidth: 140, padding: "8px 10px", border: "1px solid #D1D5DB",
    borderRadius: 6, fontSize: 13, color: "#0F1B2D", background: "white",
  };

  return (
    <form data-testid="verify-form" onSubmit={submit}
      style={{ background: "white", border: "1px solid #E5E7EB", borderLeft: "4px solid #059669",
               borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#1E3A5F" }}>
          Verify a new supplier
        </div>
        <span style={{ background: "#DCFCE7", color: "#166534", fontSize: 10, fontWeight: 700,
                        padding: "2px 8px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Live
        </span>
      </div>
      <div style={{ color: "#6B7280", fontSize: 12, marginBottom: 14 }}>
        Paste a GSTIN — get a live registration + filing verdict straight from the GST network.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <input data-testid="verify-name"  style={input} value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Supplier name *" />
        <input data-testid="verify-gstin" style={input} value={gstin} onChange={(e) => setGstin(e.target.value)}
          placeholder="GSTIN (15 chars, optional)" maxLength={15} />
        <input data-testid="verify-city"  style={input} value={city} onChange={(e) => setCity(e.target.value)}
          placeholder="City (optional)" />
        <input data-testid="verify-amt"   style={input} value={outstanding} onChange={(e) => setOutstanding(e.target.value)}
          placeholder="Outstanding ₹ (only if not in Tally)" inputMode="numeric" />
        <button data-testid="verify-submit" type="submit" disabled={busy}
          style={{ background: "#059669", color: "white", border: "none", borderRadius: 6,
                   padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer",
                   opacity: busy ? 0.7 : 1 }}>
          {busy ? "Verifying…" : "Verify"}
        </button>
      </div>
      {error && <div style={{ color: "#B91C1C", fontSize: 12, marginTop: 8 }}>{error}</div>}
      <div style={{ color: "#6B7280", fontSize: 11, marginTop: 6 }}>
        Runs GST registration and return-filing checks.
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Detail Modal — full risk drill-down
// ---------------------------------------------------------------------------
function DetailModal({ supplier, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!supplier?.id) return;
    fetch(`${BACKEND}/api/supplier/detail/${supplier.id}`)
      .then((r) => r.json()).then(setDetail).catch(() => setDetail({ supplier, checks: [] }));
  }, [supplier?.id]);

  if (!supplier) return null;
  const tone = riskTone(supplier.risk_level);
  const sp = detail?.supplier || supplier;
  const checks = detail?.checks || [];

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
        {title}
      </div>
      {children}
    </div>
  );

  const Row = ({ label, value, valueColor }) => (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px dashed #F3F4F6" }}>
      <span style={{ color: "#6B7280" }}>{label}</span>
      <span style={{ color: valueColor || "#0F1B2D", fontWeight: 600 }}>{value ?? "—"}</span>
    </div>
  );

  const findCheck = (kind) => (checks || []).find((c) => c.check_type === kind)?.raw_data || {};
  const gst = findCheck("gst");
  const mca = findCheck("mca");
  const google = findCheck("google");
  const indiamart = findCheck("indiamart");

  return (
    <div data-testid="supplier-detail-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.75)", zIndex: 80,
               display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 760, borderRadius: 12, marginTop: 24, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #E5E7EB", background: "#F9FAFB",
                      display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0F1B2D" }}>{sp.supplier_name}</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
              {sp.gstin || "No GSTIN"} · {sp.city || "—"} {sp.state ? `· ${sp.state}` : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6B7280" }}>
            <X size={20} />
          </button>
        </div>

        {/* Score + Risk */}
        <div style={{ padding: 22, borderBottom: "1px solid #F3F4F6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                Composite Score
              </div>
              <div style={{ fontSize: 40, fontWeight: 900, color: scoreBarColor(sp.composite_score || 0), lineHeight: 1.1 }}>
                {sp.composite_score ?? "—"}<span style={{ fontSize: 16, color: "#9CA3AF", fontWeight: 600 }}>/100</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ height: 8, background: "#F3F4F6", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ height: 8, background: scoreBarColor(sp.composite_score || 0),
                              width: `${Math.max(2, Math.min(100, sp.composite_score || 0))}%`, transition: "width 600ms ease" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                <span>0 High Risk</span><span>40 Caution</span><span>60 Medium</span><span>80 Low</span><span>100</span>
              </div>
            </div>
            <span style={{ background: tone.bg, color: tone.fg, padding: "6px 14px", borderRadius: 999,
                            fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: tone.dot }} />
              {sp.risk_level}
            </span>
          </div>
        </div>

        {/* AI Assessment */}
        <div style={{ padding: 22, background: "#FAFBFC", borderBottom: "1px solid #F3F4F6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <Sparkles size={14} style={{ color: "#10B981" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "#10B981", textTransform: "uppercase", letterSpacing: 1 }}>
              C-ORE assessment
            </span>
          </div>
          <p data-testid="supplier-ai-assessment" style={{ fontSize: 14, lineHeight: 1.6, color: "#0F1B2D", margin: 0 }}>
            {sp.ai_assessment || "AI assessment unavailable."}
          </p>
        </div>

        {/* Detail grids */}
        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2" style={{ padding: 22, gap: 20 }}>
          <Section title="Financial exposure">
            <Row label="Outstanding amount" value={fmtINR(sp.outstanding_amount)} />
            <Row label="ITC at risk"        value={fmtINR(sp.itc_at_risk)}
              valueColor={sp.itc_at_risk > 0 ? "#B91C1C" : "#0F1B2D"} />
            <Row label="Last verified"
              value={sp.last_verified_at ? new Date(sp.last_verified_at).toLocaleString("en-IN") : "—"} />
          </Section>
          <Section title="GST">
            <Row label="Status"          value={sp.gst_status || "—"} />
            <Row label="GSTIN status"    value={gst.gstin_status || "—"} />
            <Row label="Pending returns" value={gst.returns_pending_count} />
            <Row label="Last filed"      value={gst.last_return_filed || "—"} />
          </Section>
          <Section title="Company (MCA)">
            <Row label="Company status"  value={sp.mca_status || "—"} />
            <Row label="Type"            value={mca.company_type || "—"} />
            <Row label="Incorporated"    value={mca.incorporation_year || "—"} />
            <Row label="Years"           value={sp.years_in_business || "—"} />
          </Section>
          <Section title="Public reputation">
            <Row label="Google rating"   value={sp.google_rating ? `${sp.google_rating} ★` : "—"} />
            <Row label="Reviews"         value={sp.google_review_count || 0} />
            <Row label="Sentiment"       value={google.sentiment || "—"} />
            <Row label="B2B listing"     value={indiamart.platform || "—"} />
          </Section>
        </div>

        {(google.reputation_summary || "").trim() && (
          <div style={{ padding: "0 22px 22px" }}>
            <Section title="Reputation summary">
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>{google.reputation_summary}</div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SupplierVerification() {
  const { customer, dataAvailable, isLoading } = useCustomerData();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!customer?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/supplier/list/${customer.id}`);
      const j = await r.json();
      setRows(j.suppliers || []);
      setStats(j.stats || {});
    } catch (_e) { setRows([]); setStats({}); }
    finally { setLoading(false); }
  }, [customer?.id]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = rows.filter((s) =>
    !filter || (s.supplier_name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (s.gstin || "").toLowerCase().includes(filter.toLowerCase())
  );

  // Real Tally-derived ITC figure only — never treat a "—" (no Tally match yet,
  // even for manually-verified suppliers) as "From GST non-filers".
  const hasRealItc = stats.total_itc_at_risk != null && stats.total_itc_at_risk > 0;

  // Disabled pending Tally agent — do not re-enable until creditor sync is live.
  const runBatch = async () => {
    if (!customer?.id) return;
    setBatchBusy(true);
    try {
      await fetch(`${BACKEND}/api/supplier/verify-batch`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customer.id }),
      });
      await reload();
    } finally { setBatchBusy(false); }
  };

  return (
    <div data-testid="supplier-verification-page">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3" style={{ marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1 }}>
              Supplier Audit
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1E3A5F", margin: "2px 0 0" }}>
              Who can you trust with your money?
            </h2>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
              Verify any supplier's GST status on demand — live registration and filing checks.
              Automated portfolio audit from your Tally creditors: a pending capability, activated once your Tally sync is connected.
            </div>
          </div>
          <button data-testid="run-batch" disabled
            title="Activates once your Tally sync is connected and syncing creditors"
            style={{ background: "#F3F4F6", border: "1px solid #E5E7EB", color: "#9CA3AF",
                     borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700,
                     display: "inline-flex", alignItems: "center", gap: 6,
                     cursor: "not-allowed", opacity: 0.7 }}>
            <RefreshCw size={14} className="shrink-0" />
            <span>Auto-audit from Tally — coming when your Tally sync is connected</span>
          </button>
        </div>

        {/* Verify form — the working, live action on this page */}
        {customer?.id && <VerifyForm customerId={customer.id} onDone={reload} />}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4" style={{ marginBottom: 16 }}>
          <StatCard icon={ShieldCheck}   label="Suppliers Verified" value={stats.total_suppliers ?? 0} tone="blue"
            sub={stats.low_risk_count != null ? `${stats.low_risk_count} low risk` : ""} />
          <StatCard icon={AlertTriangle} label="High Risk"           value={stats.high_risk_count ?? 0} tone="red"
            sub={stats.medium_risk_count != null ? `${stats.medium_risk_count} medium risk` : ""} />
          <StatCard icon={AlertTriangle} label="ITC at Risk"
            value={hasRealItc ? fmtINR(stats.total_itc_at_risk) : "—"} tone="amber"
            sub={hasRealItc ? "From GST non-filers" : "Available once Tally creditor sync is connected"} />
          <StatCard icon={TrendingUp}    label="Average Score"       value={stats.total_suppliers ? (stats.average_score ?? 0) : "—"} tone="green"
            sub="Higher = safer" />
        </div>

        {/* Filter */}
        <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #E5E7EB",
                        display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0F1B2D" }}>
              All suppliers <span style={{ color: "#9CA3AF", fontWeight: 400, fontSize: 12 }}>· worst first</span>
            </span>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 8, top: 8, color: "#9CA3AF" }} />
              <input data-testid="supplier-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                placeholder="Search name or GSTIN…"
                className="w-[160px] tablet:w-[190px] desktop:w-[220px]"
                style={{ padding: "6px 10px 6px 26px", fontSize: 12, border: "1px solid #D1D5DB",
                         borderRadius: 6, color: "#0F1B2D", background: "white" }} />
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table data-testid="supplier-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Supplier", "GSTIN", "Outstanding", "ITC at risk", "Score", "Risk", ""].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", fontSize: 11, color: "#6B7280", textAlign: "left",
                                          textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #E5E7EB", fontWeight: 700 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
                    {customer?.id
                      ? "Verify suppliers individually above. Automated portfolio audit from your Tally creditors activates once Tally sync is connected."
                      : "Log in to see your verified suppliers."}
                  </td></tr>
                )}
                {!loading && filtered.map((s) => {
                  const tone = riskTone(s.risk_level);
                  const sc = s.composite_score || 0;
                  return (
                    <tr key={s.id} data-testid={`supplier-row-${s.id}`}
                      onClick={() => setSelected(s)}
                      style={{ cursor: "pointer", borderTop: "1px solid #F3F4F6" }}>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: "#0F1B2D" }}>
                        {s.supplier_name}
                        <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 400 }}>
                          {s.city || "—"} {s.gst_status ? `· ${s.gst_status}` : ""}
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: "#374151", fontFamily: "Menlo, monospace" }}>
                        {s.gstin || "—"}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: "#0F1B2D", fontWeight: 600 }}>
                        {fmtINR(s.outstanding_amount)}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: s.itc_at_risk > 0 ? "#B91C1C" : "#9CA3AF", fontWeight: 600 }}>
                        {fmtINR(s.itc_at_risk)}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: scoreBarColor(sc), width: 120 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span>{sc}</span>
                          <div style={{ flex: 1, height: 6, background: "#F3F4F6", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: 6, width: `${Math.max(2, Math.min(100, sc))}%`,
                                          background: scoreBarColor(sc), transition: "width 400ms ease" }} />
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ background: tone.bg, color: tone.fg, padding: "2px 10px",
                                       borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                          {s.risk_level || "unknown"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: "#1D4ED8", fontWeight: 600 }}>
                        View →
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && <DetailModal supplier={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
